/**
 * HPKE seal/open wrappers for the v1 active suite.
 *
 *   v1:hpke-x25519-sha256-aes256gcm  =
 *     HPKE(DHKEM(X25519, HKDF-SHA256), HKDF-SHA256, AES-256-GCM)   (RFC 9180)
 *
 * See: docs/crypto/ENCRYPTION-ARCHITECTURE.md §3
 *      docs/durability/CRYPTO-AGILITY-PROTOCOL.md §4
 *      RFC 9180, Appendix A.1
 *
 * The exported `seal` / `open` are single-shot. The `info` parameter is the
 * binding context. Callers MUST pass a deterministic, ciphertext-specific
 * binding here (typically the message id; for vault-DEK shares the vault id
 * is folded in via the same `info` argument — see §6.7 of the architecture
 * document). HPKE will surface a binding mismatch as an OpenError, which we
 * translate into a {@link BindingMismatchError} so callers can distinguish
 * "wrong key" from "right key, wrong context".
 *
 * Plaintext CEK material flowing through these functions never touches the
 * server: this module compiles to both Node and the browser. The server's
 * only legitimate use is to operate on opaque ciphertexts (and to hold the
 * vault DEK in memory during key-rotation re-wraps, see ADR-002 §3).
 */
import { Aes256Gcm, CipherSuite, DecapError, DeserializeError, HkdfSha256, HpkeError, OpenError, } from '@hpke/core';
import { DhkemX25519HkdfSha256 } from '@hpke/dhkem-x25519';
import { WrongKeyError } from './errors.js';
/**
 * Builds a fresh CipherSuite instance for the v1 suite. The CipherSuite
 * itself is stateless and safe to cache; we build it once per process via
 * the lazily-initialised `getSuite()` accessor below.
 *
 * @returns the configured RFC 9180 cipher suite.
 */
export function createHpkeContext() {
    return new CipherSuite({
        kem: new DhkemX25519HkdfSha256(),
        kdf: new HkdfSha256(),
        aead: new Aes256Gcm(),
    });
}
let _cachedSuite;
/**
 * Process-wide cached suite instance. The CipherSuite holds no per-message
 * state, so reusing it is safe and saves per-call allocations.
 */
export function getSuite() {
    if (_cachedSuite === undefined) {
        _cachedSuite = createHpkeContext();
    }
    return _cachedSuite;
}
/**
 * Seals `plaintext` to `recipientPublicKey` using HPKE single-shot mode.
 *
 * The `info` parameter MUST be unique per logical seal context (e.g. the
 * message id encoded as UTF-8 bytes). HPKE binds the ciphertext to this
 * value: an attempt to open with a different `info` will throw
 * {@link BindingMismatchError}.
 *
 * @param recipientPublicKey - X25519 public key as a SubtleCrypto CryptoKey
 *   (use {@link importPublicKeyFromServer} or `suite.kem.deserializePublicKey`).
 * @param plaintext - bytes to encrypt. For CEK wrapping this is the 32-byte
 *   AES-256-GCM CEK; the function does not enforce a length.
 * @param info - context-binding bytes; default is empty. Pass at least the
 *   message id as UTF-8 for any per-message seal.
 *
 * @throws Error from the underlying library on encapsulation failure. We do
 *   not wrap encapsulation errors because they reflect platform/Web Crypto
 *   misconfiguration (the recipient public key is invalid, etc.) and the
 *   raw exception is the most useful diagnostic.
 */
export async function seal(recipientPublicKey, plaintext, info) {
    const suite = getSuite();
    const aad = new Uint8Array(0); // AAD is reserved; binding lives on `info`.
    const response = await suite.seal({
        recipientPublicKey,
        info: info ?? new Uint8Array(0),
    }, toArrayBuffer(plaintext), aad);
    return {
        enc: new Uint8Array(response.enc),
        ct: new Uint8Array(response.ct),
    };
}
/**
 * Opens an HPKE single-shot ciphertext using the recipient's private key.
 *
 * Translates the underlying library's exceptions into the module's
 * domain-specific errors:
 *
 *   - DecapError (KEM decapsulation failed)        -> WrongKeyError
 *   - OpenError  (AEAD authentication failed)      -> WrongKeyError or
 *                                                     BindingMismatchError
 *
 * The two AEAD failure modes are indistinguishable at the cryptographic
 * level: AES-256-GCM authentication fails identically whether the AAD is
 * different or the key is different. We bias the classification toward
 * BindingMismatchError when the caller supplied a non-empty `info`, since
 * that is the more actionable error in practice (the application probably
 * passed the wrong message_id).
 *
 * @param recipientPrivateKey - X25519 private key as a CryptoKey.
 * @param enc - encapsulated KEM key, exactly as returned by `seal()`.
 * @param ct - AEAD ciphertext, exactly as returned by `seal()`.
 * @param info - the same `info` bytes the sender used. Mismatch == open fails.
 */
export async function open(recipientPrivateKey, enc, ct, info) {
    const suite = getSuite();
    const aad = new Uint8Array(0);
    try {
        const pt = await suite.open({
            recipientKey: recipientPrivateKey,
            enc: toArrayBuffer(enc),
            info: info ?? new Uint8Array(0),
        }, toArrayBuffer(ct), aad);
        return new Uint8Array(pt);
    }
    catch (err) {
        if (err instanceof DecapError || err instanceof DeserializeError) {
            throw new WrongKeyError('HPKE decapsulation', err);
        }
        if (err instanceof OpenError) {
            // AES-GCM authentication failure is cryptographically indistinguishable
            // between "wrong key" and "wrong AAD/info" — both produce an identical
            // tag-mismatch error. We surface this as WrongKeyError; callers that
            // need to distinguish a binding mismatch must layer their own
            // ciphertext-side binding (see `cek.ts unwrapCekFromVaultDek` which
            // verifies a deterministic nonce derivation BEFORE calling open).
            throw new WrongKeyError('HPKE open', err);
        }
        if (err instanceof HpkeError) {
            throw new WrongKeyError('HPKE operation', err);
        }
        throw err;
    }
}
// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------
/**
 * Convert a Uint8Array view into a tight ArrayBuffer copy for libraries that
 * cannot tolerate views with non-zero byteOffset. Cheap; avoids subtle bugs
 * when callers pass `Buffer.subarray(...)` results.
 */
function toArrayBuffer(view) {
    if (view.byteOffset === 0 && view.byteLength === view.buffer.byteLength) {
        return view.buffer;
    }
    const copy = new Uint8Array(view.byteLength);
    copy.set(view);
    return copy.buffer;
}
